import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {
    phoneBadgeSx,
    phoneCardHeadSx,
    phoneCardSx,
    phoneFactLabelSx,
    phoneFactValueSx,
    phoneFactsSx,
    phonePersonBadgeSx,
    phoneSubtitleSx,
    phoneTitleSx,
} from 'src/components/mobile/mobileDesign.sx';

/**
 * The record page's identity card, at the phone's scale.
 *
 * Everything here is the design language's own token with the record-page sizes laid over it: a
 * record's badge is 40px where a list row's is 36, its name 16px where a row's is 15, its facts 14px
 * where a row's are 13. The colours, gradients and the card's own surface are the tokens' and are
 * not restated.
 */
export const cardSx: SxProps<Theme> = phoneCardSx;

/** Badge, name, status. The tint fades out under the name, as the canvas draws it. */
export const headSx: SxProps<Theme> = {
    ...(phoneCardHeadSx as object),
    alignItems: 'center',
    padding: '14px',
    background: `linear-gradient(180deg, ${theme.palette.primaryColor.A50} 0%, ${theme.palette.common.white} 100%)`,
};

/**
 * A photograph dropped into the badge fills it rather than sitting inside it.
 *
 * A person's page passes their avatar, a charger's its photograph and a business's its logo - all
 * three are the record's own picture and are better than a glyph standing in for one. A page with no
 * picture passes an icon and the gradient shows through behind it.
 */
const badgeContentSx = {
    width: 40,
    height: 40,
    overflow: 'hidden',
    '& .MuiAvatar-root': {
        width: '100%',
        height: '100%',
        background: 'transparent',
        color: 'inherit',
        fontSize: '15px',
        fontWeight: 600,
    },
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
};

export const thingBadgeSx: SxProps<Theme> = {
    ...(phoneBadgeSx as object),
    ...badgeContentSx,
};

export const personBadgeSx: SxProps<Theme> = {
    ...(phonePersonBadgeSx as object),
    ...badgeContentSx,
};

export const identitySx: SxProps<Theme> = {
    minWidth: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
};

export const titleSx: SxProps<Theme> = {
    ...(phoneTitleSx as object),
    fontSize: '16px',
};

/**
 * The context line, allowed to wrap onto a second line.
 *
 * A list row truncates it because the rows must stay the same height; a record page has one subject
 * and an address that ends in an ellipsis is the wrong half of the address.
 */
export const subtitleSx: SxProps<Theme> = {
    ...(phoneSubtitleSx as object),
    whiteSpace: 'normal',
    overflow: 'visible',
};

export const statusSx: SxProps<Theme> = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
};

/** Two to a line, label over value, at the spacing the canvas uses on a record. */
export const factsSx: SxProps<Theme> = {
    ...(phoneFactsSx as object),
    gap: '14px',
    padding: '14px',
};

export const factSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0,
};

export const factLabelSx: SxProps<Theme> = phoneFactLabelSx;

/**
 * A value that may be a link, a price or two stacked lines.
 *
 * Unlike a list row's, it is not clipped to one line: the facts on a record page are the answer the
 * reader came for, and an address is not an answer with its second half cut off.
 */
export const factValueSx: SxProps<Theme> = {
    ...(phoneFactValueSx as object),
    fontSize: '14px',
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
};
